import { CROCHET_NOMINAL_HEIGHT } from './types';
import type { Primitive } from './geometry';

export type Bounds = {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
};

export type Bbox = { w: number; h: number };

export const NOMINAL_CROCHET_STROKE_WIDTH = CROCHET_NOMINAL_HEIGHT * 0.08;
export const NORMALIZED_NOMINAL_STROKE_RADIUS =
    (NOMINAL_CROCHET_STROKE_WIDTH / 2) / CROCHET_NOMINAL_HEIGHT;

function boundsFromPoints(
    points: Array<{ x: number; y: number }>,
    expand: number,
): Bounds {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const p of points) {
        minX = Math.min(minX, p.x);
        maxX = Math.max(maxX, p.x);
        minY = Math.min(minY, p.y);
        maxY = Math.max(maxY, p.y);
    }
    return {
        minX: minX - expand,
        maxX: maxX + expand,
        minY: minY - expand,
        maxY: maxY + expand,
    };
}

function unionBounds(a: Bounds, b: Bounds): Bounds {
    return {
        minX: Math.min(a.minX, b.minX),
        maxX: Math.max(a.maxX, b.maxX),
        minY: Math.min(a.minY, b.minY),
        maxY: Math.max(a.maxY, b.maxY),
    };
}

export function measurePrimitiveBounds(
    primitive: Primitive,
    strokeRadius: number,
): Bounds {
    switch (primitive.kind) {
        case 'line':
            return boundsFromPoints([primitive.from, primitive.to], strokeRadius);
        case 'radialLine':
            return boundsFromPoints(
                [
                    { x: primitive.x, y: primitive.y0 },
                    { x: primitive.x, y: primitive.y1 },
                ],
                strokeRadius,
            );
        case 'tangentialArc':
            return boundsFromPoints(
                [
                    { x: primitive.x0, y: primitive.y },
                    { x: primitive.x1, y: primitive.y },
                ],
                strokeRadius,
            );
        case 'dot':
            return {
                minX: primitive.cx - primitive.r,
                maxX: primitive.cx + primitive.r,
                minY: primitive.cy - primitive.r,
                maxY: primitive.cy + primitive.r,
            };
        case 'ellipse': {
            const cos = Math.cos(primitive.rotation);
            const sin = Math.sin(primitive.rotation);
            const halfW = Math.sqrt((primitive.rx * cos) ** 2 + (primitive.ry * sin) ** 2);
            const halfH = Math.sqrt((primitive.rx * sin) ** 2 + (primitive.ry * cos) ** 2);
            return {
                minX: primitive.cx - halfW - strokeRadius,
                maxX: primitive.cx + halfW + strokeRadius,
                minY: primitive.cy - halfH - strokeRadius,
                maxY: primitive.cy + halfH + strokeRadius,
            };
        }
        default: {
            const _exhaustive: never = primitive;
            throw new Error(`Unhandled primitive kind: ${(_exhaustive as Primitive).kind}`);
        }
    }
}

export function measurePrimitivesBounds(
    primitives: readonly Primitive[],
    strokeRadius: number,
): Bounds {
    if (primitives.length === 0) {
        throw new Error('measurePrimitivesBounds: primitives must not be empty');
    }
    let bounds = measurePrimitiveBounds(primitives[0], strokeRadius);
    for (let i = 1; i < primitives.length; i++) {
        bounds = unionBounds(bounds, measurePrimitiveBounds(primitives[i], strokeRadius));
    }
    return bounds;
}

function round2(value: number): number {
    return Math.round(value * 100) / 100;
}

export function deriveBboxFromPrimitives(
    primitives: readonly Primitive[],
    options: {
        nominalHeight?: number;
        strokeRadius?: number;
    } = {},
): Bbox {
    const nominalHeight = options.nominalHeight ?? CROCHET_NOMINAL_HEIGHT;
    const strokeRadius = options.strokeRadius ?? NORMALIZED_NOMINAL_STROKE_RADIUS;
    const bounds = measurePrimitivesBounds(primitives, strokeRadius);
    return {
        w: round2((bounds.maxX - bounds.minX) * nominalHeight),
        h: round2((bounds.maxY - bounds.minY) * nominalHeight),
    };
}
